import React, { useEffect, useState } from "react";

import ls from "localstorage-slim";
import { Col, Container, Image, Modal, Row } from "react-bootstrap";
import Gallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { useSearchParams } from "react-router-dom";

import { displayProjectInfo } from "@/utils/Helper";
import { getCurrentLang } from "@/utils/Local";

import DocumentComponent from "../components/Document.component";
import ErrorComponent from "../components/Error.component";
import LoaderComponent from "../components/Loader.component";
import ScheduleComponent from "../components/Schedule.component";
import i18n from "../i18n";
import apiService from "../services/api";

import type { IGalleryImage } from "@/interfaces/IGalleryImage";
import type { IProject, IProjectImage } from "@/interfaces/IProject";

const Project = () => {
  const { t } = i18n;
  const lang = getCurrentLang();
  const [searchParams] = useSearchParams();
  const projectId = (searchParams.get("id") ?? "").toLowerCase();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [project, setProject] = useState<IProject>();
  const [showGallery, setShowGallery] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const openGallery = (index: number) => {
    setCurrentIndex(index);
    setShowGallery(true);
  };

  const fetchProject = async () => {
    try {
      const cacheProjectKey = `project:${projectId}#`;

      const cachedProject = ls.get(cacheProjectKey, {
        decrypt: true,
      }) as IProject;
      if (cachedProject) {
        setProject(cachedProject);
      } else {
        const response = await apiService.fetchProject(projectId);
        if (response.status === 200 && response.data?.project) {
          const projectData = response.data.project as IProject;
          ls.set(cacheProjectKey, projectData, { ttl: 1810, encrypt: true });
          setProject(projectData);
        }
      }
    } catch (err: any) {
      if (err.response?.status && err.response.status === 404) {
        // not found
      } else {
        setError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (isLoading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  if (!project) {
    return <React.Fragment>project not found</React.Fragment>;
  }

  const images: IGalleryImage[] = project.images.map(
    (item: IProjectImage, index: number) => ({
      original: item.original,
      thumbnail: item.thumbnail,
      description: `${project?.title[lang]} (${index + 1})`,
      alt: `${project?.title[lang]} (${index + 1})`,
    }),
  );

  const renderThumbnails = (start = 0, end = 0) => {
    const items = project.images.slice(start, end);
    const thumbnailClass = "mw-100 w-100 h-100 object-fit-cover rounded";
    if (items.length === 3) {
      return (
        <Row>
          {/* render first image in the batch */}
          <Col
            className="text-center mb-2 order-lg-2 text-center"
            lg={8}
            xs={12}
          >
            <Image
              rounded
              alt={`${project.title[lang]} (${start})`}
              className={thumbnailClass}
              onClick={() => openGallery(start)}
              role="button"
              src={items[0].thumbnail}
            />
          </Col>

          <Col className="text-center order-lg-1" lg={4} xs={12}>
            <Row>
              {items.map((item: IProjectImage, index: number) => {
                // skip the first image
                if (index === 0) {
                  return <span key={index} />;
                }

                return (
                  <Col key={index} className="mb-2 text-center" lg={12} xs={6}>
                    <Image
                      rounded
                      alt={`${project.title[lang]} (${index + start})`}
                      className={thumbnailClass}
                      onClick={() => openGallery(start + index)}
                      role="button"
                      src={item.thumbnail}
                    />
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      );
    }

    const itemLength = items.length;
    return (
      <Row>
        {items.map((item: IProjectImage, index: number) => {
          let gridSize = 6;
          if (itemLength === 2 && end === project.images.length) {
            if (index === 0) {
              gridSize = 4;
            } else {
              gridSize = 8;
            }
          } else if (itemLength === 1) {
            gridSize = 12;
          }
          return (
            <Col
              key={index}
              className="mt-3 mb-3 text-center rounded"
              xs={gridSize}
            >
              <Image
                rounded
                alt={`${project.title[lang]} (${index + start})`}
                className={thumbnailClass}
                onClick={() => openGallery(start + index)}
                role="button"
                src={item.thumbnail}
              />
            </Col>
          );
        })}
      </Row>
    );
  };

  const cleanTitle = project.title[lang]
    .replaceAll("?", "")
    .replaceAll(" ", "_");

  return (
    <React.Fragment>
      <DocumentComponent
        title={`${t("project")} > ${project.title[lang]}`}
        og={{
          description: cleanTitle,
          image: {
            src: project.images[0].original,
            type: "image/jpg",
            width: 800,
            height: 450,
          },
        }}
      />

      <Modal
        fullscreen
        className="modalGallery"
        data-bs-theme="dark"
        onHide={() => setShowGallery(false)}
        show={showGallery}
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <Gallery
            items={images}
            showThumbnails={false}
            startIndex={currentIndex}
          />
        </Modal.Body>
      </Modal>

      <Container className="mb-4 project-container">
        <Row>
          <Col className="mt-5 mb-3" lg={{ span: 10, offset: 1 }} xs={12}>
            <Row>
              <Col className="mb-4" xs={12}>
                <h1>{project.title[lang]}</h1>
                <p>{project.description[lang]}</p>
              </Col>

              <Col className="mb-4" xs={12}>
                <Image
                  rounded
                  alt={project.title[lang]}
                  className="mw-100"
                  src={project.images[0].original}
                />
              </Col>
            </Row>
            <Row className="mb-4">
              <Col xs={12}>
                <div className="project-info-container">
                  {displayProjectInfo(project, t)}
                </div>
              </Col>
            </Row>

            {renderThumbnails(1, 4)}
            {renderThumbnails(4, 5)}
            {renderThumbnails(5, 7)}
            {renderThumbnails(7, 8)}
            {renderThumbnails(8, 10)}
          </Col>
        </Row>
      </Container>
      <ScheduleComponent />
    </React.Fragment>
  );
};

export default Project;
